import React, { createContext, useContext, useState, useEffect } from 'react';
import axios from 'axios';
import theLogo from '../assets/signin_login_logo.png';

const BrandingContext = createContext();

export const useBranding = () => {
    const context = useContext(BrandingContext);
    if (!context) {
        throw new Error('useBranding must be used within a BrandingProvider');
    }
    return context;
};

export const BrandingProvider = ({ children }) => {
    const [brandingSettings, setBrandingSettings] = useState({
        platformName: 'RoadVision AI',
        logo: theLogo,
        themeColors: {
            primary: '#1c4280',
            secondary: '#0190cf',
            accent: '#34A853',
            background: '#F8F9FA',
            text: '#202124'
        }
    });
    const [isLoading, setIsLoading] = useState(true);
    const [error, setError] = useState(null);

    // Fetch organization branding settings from backend
    const fetchBrandingSettings = async () => {
        try {
            setIsLoading(true);
            const response = await axios.get(
                `${process.env.REACT_APP_BASE_URL}profile/get_organization_branding`
            );

            if (response.data && response.data.result) {
                const { platformName, logo, themeColors } = response.data.result;
                setBrandingSettings({
                    platformName: platformName || 'RoadVision AI',
                    logo: logo || theLogo,
                    themeColors: {
                        primary: themeColors?.primary || '#1c4280',
                        secondary: themeColors?.secondary || '#0190cf',
                        accent: themeColors?.accent || '#34A853',
                        background: themeColors?.background || '#F8F9FA',
                        text: themeColors?.text || '#202124'
                    }
                });
                setError(null);
            }
        } catch (err) {
            console.error('Error fetching branding settings:', err);
            setError(err.message);
            // Fall back to localStorage if API fails
            const savedBranding = localStorage.getItem('brandingSettings');
            if (savedBranding) {
                try {
                    const parsed = JSON.parse(savedBranding);
                    setBrandingSettings(prev => ({
                        ...prev,
                        ...parsed
                    }));
                } catch (parseError) {
                    console.error('Error parsing localStorage branding:', parseError);
                }
            }
        } finally {
            setIsLoading(false);
        }
    };

    // Load branding settings from backend on initialization
    // useEffect(() => {
    //     fetchBrandingSettings();
    // }, []);

    // Apply theme colors to CSS custom properties
    useEffect(() => {
        const root = document.documentElement;
        const { themeColors } = brandingSettings;

        console.log('🎨 Applying theme colors to CSS:', themeColors);

        // Set CSS custom properties for theme colors
        root.style.setProperty('--primary-color', themeColors.primary);
        root.style.setProperty('--secondary-color', themeColors.secondary);
        root.style.setProperty('--accent-color', themeColors.accent);
        root.style.setProperty('--background-color', themeColors.background);
        root.style.setProperty('--text-color', themeColors.text);

        // Set additional derived colors
        root.style.setProperty('--primary-hover', darkenColor(themeColors.primary, 10));
        root.style.setProperty('--primary-light', lightenColor(themeColors.primary, 20));
        root.style.setProperty('--primary-very-light', lightenColor(themeColors.primary, 40));
        root.style.setProperty('--primary-ultra-light', lightenColor(themeColors.primary, 50));
        root.style.setProperty('--secondary-light', lightenColor(themeColors.secondary, 20));

        // Set contrast colors for text readability
        root.style.setProperty('--primary-text-color', getContrastColor(themeColors.primary));
        root.style.setProperty('--secondary-text-color', getContrastColor(themeColors.secondary));
        root.style.setProperty('--accent-text-color', getContrastColor(themeColors.accent));
        root.style.setProperty('--background-text-color', getReadableTextColor(themeColors.background));

        // Set inverse colors for special cases
        root.style.setProperty('--primary-inverse', getContrastColor(themeColors.primary));
        root.style.setProperty('--secondary-inverse', getContrastColor(themeColors.secondary));

        console.log('✅ CSS variables successfully applied:', {
            primary: root.style.getPropertyValue('--primary-color'),
            secondary: root.style.getPropertyValue('--secondary-color'),
            accent: root.style.getPropertyValue('--accent-color'),
            primaryText: root.style.getPropertyValue('--primary-text-color'),
            secondaryText: root.style.getPropertyValue('--secondary-text-color')
        });

        // Force style recalculation for all elements
        // This ensures inline styles and classes are re-evaluated
        document.body.classList.add('theme-updating');
        setTimeout(() => {
            document.body.classList.remove('theme-updating');
        }, 10);

        // Dispatch a custom event to notify all components of theme change
        const themeChangeEvent = new CustomEvent('themeChanged', {
            detail: { themeColors }
        });
        window.dispatchEvent(themeChangeEvent);

        console.log('🔄 Theme colors applied globally - UI should update immediately');

    }, [brandingSettings.themeColors]);

    // Update document title when platform name changes
    useEffect(() => {
        document.title = brandingSettings.platformName;
    }, [brandingSettings.platformName]);

    const updateBrandingSettings = async (newSettings) => {
        try {
            // Optimistically update local state
            setBrandingSettings(prev => {
                const updated = { ...prev, ...newSettings };
                return updated;
            });


            const res = await axios.get(
                `${process.env.REACT_APP_BASE_URL}profile/get_organization_branding`
            );
            // Save to backend
            const response = await axios.put(
                `${process.env.REACT_APP_BASE_URL}profile/update_organization_branding`,
                newSettings
            );

            if (response.data && response.data.result) {
                const { platformName, logo, themeColors } = response.data.result;
                setBrandingSettings({
                    platformName: platformName || 'RoadVision AI',
                    logo: logo || theLogo,
                    themeColors: {
                        primary: themeColors?.primary || '#1c4280',
                        secondary: themeColors?.secondary || '#0190cf',
                        accent: themeColors?.accent || '#34A853',
                        background: themeColors?.background || '#F8F9FA',
                        text: themeColors?.text || '#202124'
                    }
                });
            }

            // Also save to localStorage as backup
            localStorage.setItem('brandingSettings', JSON.stringify(newSettings));

            return { success: true };
        } catch (error) {
            console.error('Error updating branding settings:', error);
            // Revert optimistic update on error
            await fetchBrandingSettings();
            return { success: false, error: error.message };
        }
    };

    const updatePlatformName = async (name) => {
        const result = await updateBrandingSettings({ platformName: name });

        // Update any hardcoded platform references in the DOM
        const elementsToUpdate = document.querySelectorAll('[data-platform-name]');
        elementsToUpdate.forEach(element => {
            if (element.textContent.includes(brandingSettings.platformName)) {
                element.textContent = element.textContent.replace(
                    new RegExp(brandingSettings.platformName, 'g'),
                    name
                );
            }
        });

        return result;
    };

    const updateLogo = async (logoUrl) => {
        return await updateBrandingSettings({ logo: logoUrl });
    };

    const updateThemeColors = async (colors) => {
        console.log('Updating theme colors:', colors);
        const newThemeColors = { ...brandingSettings.themeColors, ...colors };
        console.log('New theme colors:', newThemeColors);
        return await updateBrandingSettings({
            themeColors: newThemeColors
        });
    };

    const resetToDefault = async () => {
        const defaultSettings = {
            platformName: 'RoadVision AI',
            logo: '',
            themeColors: {
                primary: '#1c4280',
                secondary: '#0190cf',
                accent: '#34A853',
                background: '#F8F9FA',
                text: '#202124'
            }
        };
        return await updateBrandingSettings(defaultSettings);
    };

    const value = {
        brandingSettings,
        isLoading,
        error,
        updateBrandingSettings,
        updatePlatformName,
        updateLogo,
        updateThemeColors,
        resetToDefault,
        refetchBranding: fetchBrandingSettings
    };

    return (
        <BrandingContext.Provider value={value}>
            {children}
        </BrandingContext.Provider>
    );
};

// Helper functions for color manipulation
const darkenColor = (color, percent) => {
    const num = parseInt(color.replace("#", ""), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) - amt;
    const G = (num >> 8 & 0x00FF) - amt;
    const B = (num & 0x0000FF) - amt;
    return "#" + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
        (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
        (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
};

const lightenColor = (color, percent) => {
    const num = parseInt(color.replace("#", ""), 16);
    const amt = Math.round(2.55 * percent);
    const R = (num >> 16) + amt;
    const G = (num >> 8 & 0x00FF) + amt;
    const B = (num & 0x0000FF) + amt;
    return "#" + (0x1000000 + (R < 255 ? R < 1 ? 0 : R : 255) * 0x10000 +
        (G < 255 ? G < 1 ? 0 : G : 255) * 0x100 +
        (B < 255 ? B < 1 ? 0 : B : 255)).toString(16).slice(1);
};

// Calculate if a color is light or dark for contrast
const getLuminance = (color) => {
    const hex = color.replace('#', '');
    const r = parseInt(hex.substr(0, 2), 16);
    const g = parseInt(hex.substr(2, 2), 16);
    const b = parseInt(hex.substr(4, 2), 16);

    // Calculate relative luminance
    const [rs, gs, bs] = [r, g, b].map(c => {
        c = c / 255;
        return c <= 0.03928 ? c / 12.92 : Math.pow((c + 0.055) / 1.055, 2.4);
    });

    return 0.2126 * rs + 0.7152 * gs + 0.0722 * bs;
};

const getContrastColor = (backgroundColor) => {
    const luminance = getLuminance(backgroundColor);
    return luminance > 0.5 ? '#000000' : '#FFFFFF';
};

const getReadableTextColor = (backgroundColor) => {
    const luminance = getLuminance(backgroundColor);
    return luminance > 0.5 ? '#1F2937' : '#F9FAFB'; // Dark gray or light gray
};

export default BrandingContext;